import type {
  AddressAnswer,
  FileUploadAccept,
  FormItemConfig,
  TimeMinuteStep,
  UploadedFileAnswer,
} from '@/types/formsEngine';

// ─── TIME ───────────────────────────────────────────────────────────────────

export const TIME_MINUTE_STEPS: readonly TimeMinuteStep[] = [5, 10, 15, 30, 60];
export const DEFAULT_MINUTE_STEP: TimeMinuteStep = 30;

export function minuteStepOf(config: FormItemConfig | null | undefined): TimeMinuteStep {
  const step = config?.minuteStep;
  return step && TIME_MINUTE_STEPS.includes(step) ? step : DEFAULT_MINUTE_STEP;
}

/** `HH:mm`, 24-hour. */
export function isTimeValue(value: unknown): value is string {
  return typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/** "14:30" → "2:30 PM". Anything that is not `HH:mm` is returned unchanged. */
export function formatTime12h(value: string): string {
  if (!isTimeValue(value)) return value;
  const [hours, minutes] = value.split(':').map(Number) as [number, number];
  const period = hours < 12 ? 'AM' : 'PM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
}

/** Mirrors the server: `HH:mm`, and on the item's minute step when one is configured. */
export function timeIssue(
  label: string,
  value: unknown,
  config: FormItemConfig | null | undefined,
): string | null {
  if (!isTimeValue(value)) return `"${label}" must be a time between 00:00 and 23:59 (HH:mm).`;
  const step = config?.minuteStep;
  if (step !== undefined && Number(value.slice(3)) % step !== 0) {
    return `"${label}" must be on a ${step}-minute step.`;
  }
  return null;
}

export interface TimeSlot {
  value: string;
  label: string;
}

/** Every slot of the day from 00:00, `step` minutes apart, in chronological order. */
export function timeSlots(step: TimeMinuteStep = DEFAULT_MINUTE_STEP): TimeSlot[] {
  const safeStep = TIME_MINUTE_STEPS.includes(step) ? step : DEFAULT_MINUTE_STEP;
  const slots: TimeSlot[] = [];
  for (let minute = 0; minute < 24 * 60; minute += safeStep) {
    const value = `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
    slots.push({ value, label: formatTime12h(value) });
  }
  return slots;
}

// ─── ADDRESS ────────────────────────────────────────────────────────────────

export const ADDRESS_FIELDS = [
  'street',
  'street2',
  'city',
  'state',
  'postalCode',
  'country',
] as const satisfies ReadonlyArray<keyof AddressAnswer>;

export function emptyAddress(): AddressAnswer {
  return { street: '', street2: '', city: '', state: '', postalCode: '', country: '' };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** A draft or stored address as editable strings; anything else is an empty address. */
export function addressDraft(value: unknown): AddressAnswer {
  const record = asRecord(value);
  const draft = emptyAddress();
  if (!record) return draft;
  for (const field of ADDRESS_FIELDS) {
    if (typeof record[field] === 'string') draft[field] = record[field];
  }
  return draft;
}

/** True when no address field holds anything but whitespace. */
export function isBlankAddress(value: unknown): boolean {
  const draft = addressDraft(value);
  return ADDRESS_FIELDS.every((field) => !draft[field]?.trim());
}

/**
 * The submitted shape: trimmed, optional parts omitted when blank, and `null`
 * for an address with nothing in it.
 */
export function normalizeAddress(value: unknown): AddressAnswer | null {
  if (isBlankAddress(value)) return null;
  const draft = addressDraft(value);
  const address: AddressAnswer = { street: draft.street.trim(), city: draft.city.trim() };
  for (const field of ['street2', 'state', 'postalCode', 'country'] as const) {
    const part = draft[field]?.trim();
    if (part) address[field] = part;
  }
  return address;
}

export const ADDRESS_FIELD_MAX_LENGTH = 200;

/**
 * Mirrors the server's ADDRESS rules: known string parts of at most 200
 * characters, and a filled address needs at least a street and a city.
 */
export function addressIssue(label: string, value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const record = asRecord(value);
  if (!record) return `"${label}" must be an address.`;
  for (const [key, part] of Object.entries(record)) {
    if (!(ADDRESS_FIELDS as readonly string[]).includes(key)) {
      return `"${label}" has an unknown address field: ${key}.`;
    }
    if (part === undefined || part === null) continue;
    if (typeof part !== 'string') return `"${label}" address field ${key} must be text.`;
    if (part.trim().length > ADDRESS_FIELD_MAX_LENGTH) {
      return `"${label}" address field ${key} must be at most ${ADDRESS_FIELD_MAX_LENGTH} characters.`;
    }
  }
  if (isBlankAddress(value)) return null;
  const filled = (part: unknown) => typeof part === 'string' && part.trim().length > 0;
  if (!filled(record.street) || !filled(record.city)) {
    return `"${label}" needs at least a street and city.`;
  }
  // Mirrors the server: platform addresses are US or Canadian.
  const country = typeof record.country === 'string' ? record.country.trim().toUpperCase() : '';
  return country && country !== 'US' && country !== 'CA'
    ? `"${label}" must be a US or Canadian address.`
    : null;
}

/** Display lines: street, street2, "City, ST 12345", country. */
export function formatAddressLines(value: unknown): string[] {
  const address = normalizeAddress(value);
  if (!address) return [];
  const region = [address.state, address.postalCode].filter(Boolean).join(' ');
  const locality = [address.city, region].filter(Boolean).join(', ');
  return [address.street, address.street2, locality, address.country].filter(
    (line): line is string => Boolean(line),
  );
}

// ─── FILE_UPLOAD ────────────────────────────────────────────────────────────

export const FILE_UPLOAD_DEFAULTS = {
  maxFiles: 5,
  maxFileSizeMb: 20,
  accept: 'images_and_pdf' as FileUploadAccept,
};
export const FILE_UPLOAD_BOUNDS = {
  maxFiles: { min: 1, max: 10 },
  maxFileSizeMb: { min: 1, max: 25 },
};

const IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
const PDF_CONTENT_TYPE = 'application/pdf';

export interface FileUploadLimits {
  maxFiles: number;
  maxFileSizeMb: number;
  accept: FileUploadAccept;
}

function clampInteger(value: unknown, fallback: number, bounds: { min: number; max: number }) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(bounds.max, Math.max(bounds.min, Math.floor(value)));
}

export function fileUploadLimits(config: FormItemConfig | null | undefined): FileUploadLimits {
  return {
    maxFiles: clampInteger(
      config?.maxFiles,
      FILE_UPLOAD_DEFAULTS.maxFiles,
      FILE_UPLOAD_BOUNDS.maxFiles,
    ),
    maxFileSizeMb: clampInteger(
      config?.maxFileSizeMb,
      FILE_UPLOAD_DEFAULTS.maxFileSizeMb,
      FILE_UPLOAD_BOUNDS.maxFileSizeMb,
    ),
    accept: config?.accept === 'images' ? 'images' : FILE_UPLOAD_DEFAULTS.accept,
  };
}

export function acceptedContentTypes(accept: FileUploadAccept): string[] {
  return accept === 'images' ? IMAGE_CONTENT_TYPES : [...IMAGE_CONTENT_TYPES, PDF_CONTENT_TYPE];
}

/**
 * The `<input accept>` value. Listing image types keeps the camera on offer on
 * mobile; the HEIC extensions cover browsers that report no MIME type for them.
 */
export function acceptAttribute(accept: FileUploadAccept): string {
  return [...acceptedContentTypes(accept), '.heic', '.heif'].join(',');
}

export function acceptDescription(accept: FileUploadAccept): string {
  return accept === 'images'
    ? 'JPEG, PNG, HEIC or WebP images'
    : 'images (JPEG, PNG, HEIC, WebP) or PDFs';
}

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  heif: 'image/heif',
  webp: 'image/webp',
  pdf: PDF_CONTENT_TYPE,
};

/** The file's MIME type, falling back to its extension (some browsers leave HEIC blank). */
export function fileContentType(file: Pick<File, 'name' | 'type'>): string {
  const reported = file.type.trim().toLowerCase();
  if (reported) return reported === 'image/jpg' ? 'image/jpeg' : reported;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_CONTENT_TYPES[extension] ?? 'application/octet-stream';
}

export function isImageContentType(contentType: string): boolean {
  return contentType.startsWith('image/');
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface FileSelectionResult {
  accepted: File[];
  errors: string[];
}

/**
 * Split a picked batch into the files that may upload and messages for the
 * rest. `currentCount` is how many files the answer already holds (uploaded,
 * uploading or failed).
 */
export function validateFileSelection(
  files: File[],
  currentCount: number,
  config: FormItemConfig | null | undefined,
): FileSelectionResult {
  const limits = fileUploadLimits(config);
  const allowed = acceptedContentTypes(limits.accept);
  const accepted: File[] = [];
  const errors: string[] = [];
  let skippedForCount = 0;

  for (const file of files) {
    if (!allowed.includes(fileContentType(file))) {
      errors.push(
        `"${file.name}" isn't a supported file type. Upload ${acceptDescription(limits.accept)}.`,
      );
    } else if (file.size > limits.maxFileSizeMb * 1024 * 1024) {
      errors.push(`"${file.name}" is larger than ${limits.maxFileSizeMb} MB.`);
    } else if (file.size <= 0) {
      errors.push(`"${file.name}" is empty.`);
    } else if (currentCount + accepted.length >= limits.maxFiles) {
      skippedForCount += 1;
    } else {
      accepted.push(file);
    }
  }
  if (skippedForCount > 0) {
    errors.push(
      `You can upload up to ${limits.maxFiles} ${limits.maxFiles === 1 ? 'file' : 'files'}; ${skippedForCount} ${skippedForCount === 1 ? 'was' : 'were'} not added.`,
    );
  }
  return { accepted, errors };
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FILE_NAME_MAX_LENGTH = 255;
const UPLOADED_FILE_KEYS = ['uploadId', 'fileName', 'contentType', 'size'];

/** A well-formed submitted file (a staff read may also carry a `url`). */
export function isUploadedFileAnswer(value: unknown): value is UploadedFileAnswer {
  const record = asRecord(value);
  return (
    record !== null &&
    typeof record.uploadId === 'string' &&
    record.uploadId.length > 0 &&
    typeof record.fileName === 'string' &&
    typeof record.contentType === 'string' &&
    typeof record.size === 'number' &&
    Number.isFinite(record.size)
  );
}

/** The well-formed files of a FILE_UPLOAD answer; tolerates malformed values. */
export function uploadedFilesOf(value: unknown): UploadedFileAnswer[] {
  return Array.isArray(value) ? value.filter(isUploadedFileAnswer) : [];
}

/** Mirrors the server's shape and limit checks on a submitted FILE_UPLOAD value. */
export function fileUploadAnswerIssue(
  label: string,
  value: unknown,
  config: FormItemConfig | null | undefined,
): string | null {
  const invalid = `"${label}" has an invalid file list.`;
  if (!Array.isArray(value)) return invalid;
  const limits = fileUploadLimits(config);
  if (value.length > limits.maxFiles) {
    return `"${label}" allows at most ${limits.maxFiles} file(s).`;
  }
  const allowed = acceptedContentTypes(limits.accept);
  const seen = new Set<string>();
  for (const entry of value) {
    const file = asRecord(entry);
    if (
      !file ||
      Object.keys(file).some((key) => !UPLOADED_FILE_KEYS.includes(key)) ||
      typeof file.uploadId !== 'string' ||
      !UUID_PATTERN.test(file.uploadId) ||
      typeof file.fileName !== 'string' ||
      !file.fileName.trim() ||
      file.fileName.length > FILE_NAME_MAX_LENGTH ||
      typeof file.contentType !== 'string' ||
      typeof file.size !== 'number' ||
      !Number.isInteger(file.size) ||
      file.size < 1 ||
      seen.has(file.uploadId.toLowerCase())
    ) {
      return invalid;
    }
    seen.add(file.uploadId.toLowerCase());
    if (!allowed.includes(file.contentType)) {
      return `"${label}" does not accept ${file.contentType} files.`;
    }
    if (file.size > limits.maxFileSizeMb * 1024 * 1024) {
      return `"${label}" files must be ${limits.maxFileSizeMb} MB or smaller.`;
    }
  }
  return null;
}
